import { config, OAUTH_CALLBACK_PATH } from '../config/env';
import {
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
  storeOAuthState,
  storePkceVerifier,
  storeRedirectPath,
} from './pkce';
import { clearSession } from './session';

/**
 * Build the OIDC authorization URL and redirect to it.
 * This initiates the OAuth login flow with PKCE.
 */
export async function initiateLogin(redirectPath?: string): Promise<void> {
  if (
    !config.oidc.issuer ||
    !config.oidc.clientId ||
    !config.oidc.authorizeEndpoint ||
    !config.oidc.scopes
  ) {
    throw new Error('OIDC configuration is not set');
  }

  // Generate PKCE pair
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Generate state for CSRF protection
  const state = generateState();

  // Store verifier and state for the callback
  storePkceVerifier(codeVerifier);
  storeOAuthState(state);

  // Store the current path to redirect back to after login
  if (redirectPath) {
    storeRedirectPath(redirectPath);
  } else {
    storeRedirectPath(window.location.pathname + window.location.search);
  }

  // Build the redirect URI
  const redirectUri = new URL(OAUTH_CALLBACK_PATH, window.location.origin).href;

  // Build authorization URL
  const authUrl = new URL(config.oidc.authorizeEndpoint);

  authUrl.searchParams.set('client_id', config.oidc.clientId);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('scope', config.oidc.scopes);
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('state', state);
  authUrl.searchParams.set('code_challenge', codeChallenge);
  authUrl.searchParams.set('code_challenge_method', 'S256');

  // Redirect to OIDC provider
  window.location.href = authUrl.href;
}

/** Where sign-out lands. The query flag stops the app auto-logging back in. */
export const LOGGED_OUT_REDIRECT = '/?logged_out=1';

/** False only after a deliberate sign-out, so the user can stay signed out. */
export function shouldAutoLogin(search: string): boolean {
  return !new URLSearchParams(search).has('logged_out');
}

/**
 * Call the API logout endpoint to revoke tokens, then show the signed-out page.
 * This only ends the viewer session — it does NOT terminate the global Okta session,
 * so the signed-out page must not auto-login or the IdP would silently sign you
 * straight back in.
 */
export async function initiateLogout(): Promise<void> {
  try {
    const response = await fetch(`${config.apiBaseUrl}/auth/logout`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      console.warn('Logout API returned non-OK status:', response.status);
    }
  } catch (error) {
    console.error('Failed to call logout API:', error);
  }

  clearSession();
  window.location.href = LOGGED_OUT_REDIRECT;
}
